import { ColumnDef } from '@benzinga/table';

import {
  SymbolCell,
  RankingTrendCell,
  RankingScoreCell,
  RESEARCH_SYMBOL_COLUMN_WIDTH,
  rankingScoreSortValue,
  trendValueFormatter,
} from '../utils';
import { WatchlistFinancials } from '../../../data';

export const RankingsColumnsDef: ColumnDef<WatchlistFinancials>[] = [
  {
    field: 'symbol',
    headerName: 'Symbol',
    width: RESEARCH_SYMBOL_COLUMN_WIDTH,
    sortEnabled: true,
    cellRenderer: ({ data }) => <SymbolCell record={data} />,
    valueGetter: ({ data }) => data.symbol || '',
  },
  {
    field: 'momentum_ranking',
    headerName: 'Momentum',
    headerTooltip:
      "Momentum measures a stock's relative strength based on its price movement patterns and volatility over multiple timeframes, ranked as a percentile against other stocks.",
    sortEnabled: true,
    valueGetter: ({ data }) => rankingScoreSortValue(data?.rankings, 'momentum'),
    cellRenderer: ({ data }) => <RankingScoreCell rankings={data?.rankings} field="momentum" />,
  },
  {
    field: 'growth_ranking',
    headerName: 'Growth',
    headerTooltip:
      "Growth measures a stock's combined historical expansion in earnings and revenue across multiple time periods, with emphasis on both long-term trends and recent performance.",
    sortEnabled: true,
    valueGetter: ({ data }) => rankingScoreSortValue(data?.rankings, 'growth'),
    cellRenderer: ({ data }) => <RankingScoreCell rankings={data?.rankings} field="growth" />,
  },
  {
    field: 'value_ranking',
    headerName: 'Value',
    headerTooltip:
      "Value is a percentile-ranked composite metric that evaluates a stock's relative worth by comparing its market price to fundamental measures of the company's assets, earnings, sales, and operating performance.",
    sortEnabled: true,
    valueGetter: ({ data }) => rankingScoreSortValue(data?.rankings, 'value'),
    cellRenderer: ({ data }) => <RankingScoreCell rankings={data?.rankings} field="value" />,
  },
  {
    field: 'quality_ranking',
    headerName: 'Quality',
    headerTooltip:
      "Quality is a composite ranking that evaluates a company's operational efficiency and financial health by analyzing historical profitability metrics and fundamental strength indicators on a percentile basis relative to peers.",
    sortEnabled: true,
    valueGetter: ({ data }) => rankingScoreSortValue(data?.rankings, 'quality'),
    cellRenderer: ({ data }) => <RankingScoreCell rankings={data?.rankings} field="quality" />,
  },
  {
    field: 'short_term_trend',
    headerName: 'Short',
    headerTooltip: 'Shows if a stock is in an upward or downward trend over the last couple of months.',
    sortEnabled: true,
    valueGetter: ({ data }) => trendValueFormatter(data?.rankings?.shortTermTrend),
    cellRenderer: ({ data }) => <RankingTrendCell value={data?.rankings?.shortTermTrend as 'Y' | 'N' | undefined} />,
  },
  {
    field: 'medium_term_trend',
    headerName: 'Medium',
    headerTooltip: 'Shows if a stock is in an upward or downward trend over the last couple of quarters.',
    sortEnabled: true,
    valueGetter: ({ data }) => trendValueFormatter(data?.rankings?.mediumTermTrend),
    cellRenderer: ({ data }) => <RankingTrendCell value={data?.rankings?.mediumTermTrend as 'Y' | 'N' | undefined} />,
  },
  {
    field: 'long_term_trend',
    headerName: 'Long',
    headerTooltip: 'Shows if a stock is in an upward or downward trend over the past year.',
    sortEnabled: true,
    valueGetter: ({ data }) => trendValueFormatter(data?.rankings?.longTermTrend),
    cellRenderer: ({ data }) => <RankingTrendCell value={data?.rankings?.longTermTrend as 'Y' | 'N' | undefined} />,
  },
];
